// src/components/authorizations/AuthorizationDetails.tsx

import { Badge, Group, Stack, Text } from "@mantine/core";
import { formatExpiry, ScopeList } from "./authorizationFormatters";

type Props = {
  type: string; // SQL / BIGQUERY / FACEBOOK / … (or "AuthorizationType.SQL")
  config: Record<string, any>;
};

function normalizeType(type?: string): string {
  if (!type) return "";
  const parts = String(type).split(".");
  return parts[parts.length - 1];
}

/**
 * A labeled key/value row. Empty values are omitted by default so the
 * drawer doesn't show misleading "—" rows for data that a given
 * auth_method simply doesn't collect (e.g., ad_account_id on OAuth).
 * Pass `showEmpty` to force rendering with an em-dash.
 */
function Row({
  label,
  value,
  showEmpty = false,
}: {
  label: string;
  value?: unknown;
  showEmpty?: boolean;
}) {
  const isEmpty =
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);

  if (isEmpty && !showEmpty) return null;

  const display = isEmpty ? "—" : String(value);

  return (
    <Group gap="xs" justify="space-between" wrap="nowrap">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={500} style={{ textAlign: "right" }}>
        {display}
      </Text>
    </Group>
  );
}

export function AuthorizationDetails({ type, config }: Props) {
  const t = normalizeType(type);

  if (t === "BIGQUERY") {
    return (
      <Stack gap="xs">
        <Text fw={600}>Connection details</Text>
        <Row label="Project" value={config?.project_id} />
        <Row label="Client email" value={config?.client_email} />
        <Row label="Location" value={config?.location} />
      </Stack>
    );
  }

  if (t === "SQL") {
    const dialect = config?.dialect;
    return (
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600}>Connection details</Text>
          {dialect ? (
            <Badge variant="light" color="gray">
              {String(dialect).toUpperCase()}
            </Badge>
          ) : null}
        </Group>

        <Row label="Host" value={config?.host} />
        <Row label="Port" value={config?.port} />
        <Row label="Database" value={config?.database} />
        <Row label="SSL mode" value={config?.ssl_mode} />

        {String(dialect || "").toLowerCase() === "mssql" ? (
          <>
            <Row label="Encrypt" value={config?.encrypt} showEmpty />
            <Row
              label="Trust server cert"
              value={config?.trust_server_certificate}
              showEmpty
            />
          </>
        ) : null}
      </Stack>
    );
  }

  if (t === "FACEBOOK") {
    // OAuth auths cover multiple ad accounts; data sources select their
    // own ad_account later, so we deliberately don't show it here.
    const isOAuth = config?.auth_method === "oauth";

    return (
      <Stack gap="xs">
        <Text fw={600}>Connection details</Text>
        {!isOAuth && (
          <>
            <Row label="Ad Account" value={config?.ad_account_id} />
            <Row label="Account name" value={config?.account_name} />
            <Row label="Currency" value={config?.currency} />
            <Row label="Timezone" value={config?.timezone} />
          </>
        )}
        <Row label="Token expires" value={formatExpiry(config?.token_expires_at)} />
        <Row
          label="Data access expires"
          value={formatExpiry(config?.data_access_expires_at)}
        />
        <ScopeList scopes={config?.token_scopes} />
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      <Text fw={600}>Connection details</Text>
      <Text size="sm" c="dimmed">
        No type-specific details available.
      </Text>
    </Stack>
  );
}
